/**
 * 订阅逻辑 Hook（精简自 H5 src/composables/payment/composables/useSubscription.ts）
 */
import { computed, ref } from 'vue'
import { PRODUCT_LIST_API, SUBSCRIBE_API_ENDPOINT, SubscriptionCycleValue, VIP_TYPE } from './constants'
import {
  createAvailablePaymentMethods,
  createSelectPaymentMethod,
  getPayType,
  getPaymentChannelName,
  initializePaymentMethod,
} from './paymentUtils'
import { api } from '@/api'

export interface SubscriptionBook {
  contentId?: string
  contentChapterId?: string
}

export function useSubscription(book?: SubscriptionBook) {
  const loaded = ref(false)
  const payLoading = ref(false)
  const productLists = ref<any[]>([])
  const currentPaymentMethod = ref<number>(3) // 默认 PayPal
  const hasPayPalPasswordlessPayment = ref(false)
  const hasPlacedOrder = ref(false)

  const availablePaymentMethods = computed(createAvailablePaymentMethods(
    productLists,
    hasPayPalPasswordlessPayment,
    hasPlacedOrder,
  ))

  const subscribeProducts = computed(() => {
    const currentMethod = Number(currentPaymentMethod.value)
    const productList = availablePaymentMethods.value.find(
      (list: any) => Number(list.paymentMethod) === currentMethod,
    )
    if (!productList?.products)
      return []
    return productList.products.filter((p: any) => p.isShow !== false)
  })

  const selectPaymentMethod = createSelectPaymentMethod(currentPaymentMethod)

  async function fetchSubscribes() {
    try {
      const res: any = await api.get(PRODUCT_LIST_API.SUBSCRIBE)
      if (res.data && typeof res.data === 'object') {
        hasPayPalPasswordlessPayment.value = res.data.hasPayPalPasswordlessPayment || false
        hasPlacedOrder.value = res.data.hasPlacedOrder || false
        productLists.value = res.data.productLists || []
        initializePaymentMethod(currentPaymentMethod, productLists, hasPayPalPasswordlessPayment, hasPlacedOrder)
      }
    }
    catch {}
    loaded.value = true
  }

  function getUnitPrice(item: any): string {
    if (!item.cycle)
      return ''
    if (item.cycle === SubscriptionCycleValue.YEAR)
      return `${(Number(item.price) / 12).toFixed(2)}/month`
    if (item.cycle === SubscriptionCycleValue.HALF_YEAR)
      return `${(Number(item.price) / 6).toFixed(2)}/month`
    if (item.cycle === SubscriptionCycleValue.SEASON)
      return `${(Number(item.price) / 3).toFixed(2)}/month`
    if (item.cycle === SubscriptionCycleValue.MONTH)
      return `${(Number(item.price) / 30).toFixed(2)}/day`
    if (item.cycle === SubscriptionCycleValue.WEEK)
      return `${(Number(item.price) / 7).toFixed(2)}/day`
    if (item.cycle === SubscriptionCycleValue.ThreeDays)
      return `${(Number(item.price) / 3).toFixed(2)}/day`
    return ''
  }

  function isZero(val: string | number | null | undefined) {
    if (val === null || val === undefined || val === '')
      return false
    return Number.parseFloat(val as any) === 0
  }

  function isDiscount(item: any) {
    return item.subscribeType === VIP_TYPE.DISCOUNT
  }

  // 判断是否需要显示背景图（对齐 H5 useSubscription.getIsImageBackground）
  function getIsImageBackground(item: any) {
    if (item.subscribeType === VIP_TYPE.DISCOUNT)
      return true
    if (isZero(item.preferentialPrice) || item.preferentialPrice)
      return true
    return false
  }

  async function onSubscribeProducts(item: any) {
    if (payLoading.value)
      return
    payLoading.value = true
    const successUrl = book?.contentId ? window.location.href : `${window.location.origin}/member`
    const redirectUrl = `${window.location.origin}/paying?redirect_source=${encodeURIComponent(successUrl)}&paymentChannel=${getPaymentChannelName(currentPaymentMethod.value)}&axon_price=${item.price}&axon_product_id=${item.productId}`
    const params = {
      payConfigId: item.subscribeConfigId,
      productId: item.productId,
      memberCurrencyCode: 'usd',
      subscribeConfigId: item.subscribeConfigId,
      contentId: book?.contentId,
      contentChapterId: book?.contentChapterId,
      payType: getPayType(currentPaymentMethod.value),
      type: 2,
      successUrl: `${redirectUrl}&payment_status=success&type=subscribe&subscribeType=${item.subscribeType}&discountOnceCoins=${item.discountOnceCoins}`,
      cancelUrl: `${redirectUrl}&payment_status=cancel&type=subscribe&subscribeType=${item.subscribeType}`,
    }
    try {
      const { data } = await api.post<any>(SUBSCRIBE_API_ENDPOINT, params)
      if (data) {
        try {
          const _ = new URL(data)
          window.location.href = data
        }
        catch {
          window.location.reload()
        }
      }
      else {
        window.location.reload()
      }
    }
    catch {
      payLoading.value = false
    }
  }

  fetchSubscribes()

  return {
    loaded,
    payLoading,
    productLists,
    subscribeProducts,
    availablePaymentMethods,
    currentPaymentMethod,
    selectPaymentMethod,
    hasPayPalPasswordlessPayment,
    getUnitPrice,
    isZero,
    isDiscount,
    getIsImageBackground,
    onSubscribeProducts,
  }
}
